import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UIkit from "uikit";
import feather from "feather-icons";
import { formatContactPhone } from "./presentation";
import CRMService from "../../services/crm.service";
import ContactSearch from "./contact-search";

const label = (person) => [person.first_name, person.last_name].filter(Boolean).join(" ") || person.email || "Unnamed person";
const fieldLabels = { first_name: "First name", last_name: "Last name", email: "Email", phone: "Phone", primary_role: "Category" };
const fieldValue = (field) => [field.value_text, field.value_number, field.value_date, field.value_boolean]
  .find((value) => value !== null && value !== undefined && value !== "");

export default function MergePeople({ selectedPeople = [], onMerged }) {
  const [added, setAdded] = useState([]);
  const drawerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [survivor, setSurvivor] = useState("");
  const [fields, setFields] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const [error, setError] = useState("");
  const people = useMemo(() => [...new Map(added
    .filter((p) => p.crm_identity_id && p._type !== "unit")
    .map((p) => [String(p.crm_identity_id), p])).values()], [added]);
  const customGroups = useMemo(() => {
    const groups = {};
    (preview?.custom_fields || []).forEach((field) => {
      if (fieldValue(field) === undefined) return;
      const group = groups[field.field_definition_id] ||= [];
      if (!group.some((other) => JSON.stringify(fieldValue(other)) === JSON.stringify(fieldValue(field)))) group.push(field);
    });
    return Object.entries(groups).filter(([, values]) => values.length > 1);
  }, [preview]);
  const ready = preview && Object.entries(preview.field_options).every(([key, values]) => values.length < 2 || fields[key])
    && customGroups.every(([id]) => customFields[id]);
  useEffect(() => {
    if (!open) return;
    const element = drawerRef.current;
    const drawer = UIkit.offcanvas(element);
    const hidden = () => setOpen(false);
    element.addEventListener("hidden", hidden);
    drawer.show();
    return () => { element.removeEventListener("hidden", hidden); drawer.$destroy(); };
  }, [open]);
  useEffect(() => {
    const element = drawerRef.current;
    const beforeHide = event => { if (busyRef.current) event.preventDefault(); };
    element.addEventListener("beforehide", beforeHide);
    return () => element.removeEventListener("beforehide", beforeHide);
  }, []);
  const close = () => { if (!busy) UIkit.offcanvas(drawerRef.current).hide(); };
  const start = () => {
    setAdded(selectedPeople); setPreview(null); setError(""); setConfirmed(false); setOpen(true);
  };
  const load = async () => {
    if (people.length < 2 || people.length > 50) return;
    setBusy(true); setError(""); setPreview(null); setConfirmed(false);
    const keep = people[0].crm_identity_id;
    try {
      const { data } = await CRMService.previewMerge({ ids: people.map(p => p.crm_identity_id), survivor_id: keep });
      setPreview(data); setSurvivor(String(keep)); setFields({}); setCustomFields({});
    } catch (err) { setError(err.response?.data?.error || "Unable to load merge preview."); }
    finally { setBusy(false); }
  };
  const merge = async () => {
    setBusy(true); setError("");
    try {
      await CRMService.mergePeople({ ids: preview.people.map(p => p.id), survivor_id: survivor,
        revision: preview.revision, fields, custom_fields: customFields, confirmed });
      busyRef.current = false; setBusy(false); UIkit.offcanvas(drawerRef.current).hide(); setAdded([]); onMerged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to merge. No partial merge has been saved.");
      if (err.response?.status === 409) { setPreview(null); setConfirmed(false); }
    } finally { setBusy(false); }
  };
  return <>
    <button type="button" className="uk-button hms-btn hms-white-btn uk-button-small crm-icon-btn"
      aria-label="Merge people" uk-tooltip="Merge people" onClick={start}>
      <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: feather.icons["git-merge"].toSvg({ width: 15, height: 15 }) }} />
    </button>
    {createPortal(<div ref={drawerRef} id="crm-merge-drawer" className="hms-modals uk-dark"
      uk-offcanvas="flip: true; overlay: true" role="dialog" aria-modal="true" aria-labelledby="crm-merge-title">
      <div className="uk-offcanvas-bar">
        {open && <div className="crm-merge-layout">
          <header className="crm-merge-header">
            <h2 id="crm-merge-title">Merge people</h2>
            <button type="button" aria-label="Close merge people" className="crm-merge-close" disabled={busy} onClick={close}>×</button>
          </header>
          <div className="crm-merge-body" aria-busy={busy}>
            {!preview && !busy && <>
              <p>Find and select records that belong to the same person, across any category.</p>
              <ContactSearch placeholder="Find people to merge" ariaLabel="Find people to merge" onSelect={person => setAdded(previous => [...previous, person])} />
              <h3>Selected people ({people.length})</h3>
              {!people.length && <p className="crm-merge-empty">Search above to add people to merge.</p>}
              <ul className="crm-merge-people">
                {people.map(person => <li key={person.crm_identity_id}>
                  <div><strong>{label(person)}</strong><span className="crm-merge-category">{person.category || person.primary_role || person._type}</span>
                    <small>{person.email}</small><small>{formatContactPhone(person.phone || person.formatted_phone)}</small></div>
                  <button type="button" className="crm-merge-remove" aria-label={`Remove ${label(person)} from merge`}
                    onClick={() => setAdded(previous => previous.filter(p => String(p.crm_identity_id) !== String(person.crm_identity_id)))}>Remove</button>
                </li>)}
              </ul>
              {people.length > 50 && <p role="alert">Select up to 50 people per merge.</p>}
            </>}
            {error && <p role="alert">{error}</p>}
            {busy && !preview && <p role="status">Loading preview…</p>}

            {preview && <>
              <h3>Review merge</h3>
              <p>History, tasks, linked properties and reservations will be combined. Original values are retained under Merged records. Reservation and message records will not be deleted.</p>
              <label>Person record to keep
                <select className="uk-select" value={survivor} disabled={busy} onChange={e => { setSurvivor(e.target.value); setConfirmed(false); }}>
                  {preview.people.map((p, index) => <option key={p.id} value={p.id}>Record {index + 1}: {label(p)} — {p.primary_role} — {p.email || p.phone}</option>)}
                </select>
              </label>
              {Object.entries(preview.field_options).map(([key, options]) => <div className="uk-margin-small-top" key={key}>
                <label>{fieldLabels[key] || key}
                  {options.length > 1 ? <select className="uk-select" aria-label={fieldLabels[key] || key} value={fields[key] || ""} disabled={busy}
                    onChange={e => { setFields({ ...fields, [key]: e.target.value }); setConfirmed(false); }}>
                    <option value="">Choose the value to keep</option>
                    {options.map(value => <option key={value} value={value}>{value}</option>)}
                  </select> : <div>{options[0] || "Empty"}</div>}
                </label>
              </div>)}
              {customGroups.map(([id, values]) => <label className="uk-display-block uk-margin-small-top" key={id}>{values[0].label}
                <select className="uk-select" value={customFields[id] || ""} disabled={busy} onChange={e => { setCustomFields({ ...customFields, [id]: e.target.value }); setConfirmed(false); }}>
                  <option value="">Choose the value to keep</option>
                  {values.map(v => <option key={v.id} value={v.id}>{String(fieldValue(v))}</option>)}
                </select>
              </label>)}
              <label className="uk-display-block uk-margin-top"><input type="checkbox" checked={confirmed} disabled={busy} onChange={e => setConfirmed(e.target.checked)} /> I confirm these records are the same person and have reviewed the values to keep.</label>
            </>}
          </div>
          <footer className="crm-merge-footer">
            {preview && <button type="button" className="uk-button hms-btn hms-white-btn" disabled={busy} onClick={() => { setPreview(null); setConfirmed(false); setError(""); }}>Back</button>}
            <button type="button" className="uk-button hms-btn hms-white-btn" disabled={busy} onClick={close}>Cancel</button>
            <button type="button" className="uk-button hms-btn hms-red-btn" disabled={busy || (preview ? !ready || !confirmed : people.length < 2 || people.length > 50)}
              onClick={preview ? merge : load}>{busy ? (preview ? "Merging…" : "Loading…") : preview ? "Confirm merge" : error ? "Reload preview" : "Merge selected"}</button>
          </footer>
        </div>}
      </div>
    </div>, document.body)}
  </>;
}
